import { BaseCoupon as Coupon } from '@server/schemas'
import { dayjs } from '@shared/libs'

type CouponStatus = Extract<Coupon['displayStatus'], 'NORMAL' | 'USED' | 'EXPIRED' | 'PENDING'>

export const MAXIMUM_COUPON_DISCOUNT_AMOUNT = 9_999_999

export class CouponModel {
  static readonly STATUS: { [key in CouponStatus]: key } = {
    NORMAL: 'NORMAL',
    USED: 'USED',
    EXPIRED: 'EXPIRED',
    PENDING: 'PENDING',
  } as const

  static readonly DISCOUNT_TYPE = {
    FIXED: 'FIXED',
    PERCENTAGE: 'PERCENTAGE',
  } as const

  static readonly REMAINING_TIME_TYPE = {
    TIME: 'TIME',
    DDAY: 'DDAY',
    NON_DISPLAY: 'NON_DISPLAY',
  } as const

  constructor(public readonly plain: Coupon) {}

  static create(plain: Coupon) {
    return new CouponModel(plain)
  }

  get isPercentageDiscount(): boolean {
    return this.plain.discountType === 'PERCENTAGE'
  }

  get discountType(): (typeof CouponModel.DISCOUNT_TYPE)[keyof typeof CouponModel.DISCOUNT_TYPE] {
    return this.plain.discountType as (typeof CouponModel.DISCOUNT_TYPE)[keyof typeof CouponModel.DISCOUNT_TYPE]
  }

  get isFixedDiscount(): boolean {
    return this.plain.discountType === 'FIXED'
  }

  get isLumpSumPayment(): boolean {
    return this.plain.paymentTypes.includes('LUMP_SUM') || this.plain.paymentTypes.includes('IPAD')
  }

  get isSubscribePayment(): boolean {
    return this.plain.paymentTypes.includes('SUBSCRIBE')
  }

  get isAvailable(): boolean {
    return this.plain.displayStatus === 'NORMAL' || this.plain.displayStatus === 'PENDING'
  }

  get isExpired(): boolean {
    return this.plain.displayStatus === 'EXPIRED'
  }

  get isUsed(): boolean {
    return this.plain.displayStatus === 'USED'
  }

  get remainingTimeType(): (typeof CouponModel.REMAINING_TIME_TYPE)[keyof typeof CouponModel.REMAINING_TIME_TYPE] {
    return this.calculateRemainingTimeType()
  }

  get isMaximumDiscount(): boolean {
    return this.plain.discountAmountMax === MAXIMUM_COUPON_DISCOUNT_AMOUNT
  }

  get couponDisplayStartedAt(): string {
    return this.isCouponCreatedAtAfterUseStart ? this.plain.utcCreatedAt : this.plain.utcUseStart
  }

  private get isCouponCreatedAtAfterUseStart(): boolean {
    return dayjs(this.plain.utcCreatedAt).isAfter(dayjs(this.plain.utcUseStart))
  }

  private calculateRemainingTimeType(): (typeof CouponModel.REMAINING_TIME_TYPE)[keyof typeof CouponModel.REMAINING_TIME_TYPE] {
    const now = dayjs()
    const localEndDate = dayjs(dayjs(this.plain.utcUseEnd).toISOString())

    const diffHours = localEndDate.diff(now, 'hours')

    if (diffHours <= 24 && now.isBefore(localEndDate)) {
      return CouponModel.REMAINING_TIME_TYPE.TIME
    }

    if (diffHours <= 96 && now.isBefore(localEndDate)) {
      return CouponModel.REMAINING_TIME_TYPE.DDAY
    }

    return CouponModel.REMAINING_TIME_TYPE.NON_DISPLAY
  }
}
